// █▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀█
// █        JESSE STEWART         █
// █        UX/UI Designer        █
// █     www.JesseStewart.com     █
// █                              █
// █   (c) All Rights Reserved    █
// █▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄█

import React from "react";
/** @jsx jsx */ import { Global, css, jsx } from "@emotion/core";
import { Helmet } from "react-helmet";
import styled from "@emotion/styled";
import animateScrollTo from "animated-scroll-to";
import { Link, Redirect } from "react-router-dom";
import { Typeset, Typepad } from "../Partials/Typeset";
import { Button } from "../Partials/Button";
import {
  sizes,
  expandValue,
  breakpoints,
  transition
} from "../../helpers/theme";
import Typist from "react-typist";

import { getRandomInt } from "../../helpers/utils";

var rant = require("rantjs");
var tinycolor = require("tinycolor2");
var $ = require("jquery");

let TerminalContainerStyles = props => css`
  min-height: 100vh;
  padding: ${expandValue(sizes.space * 4)} ${expandValue(sizes.space * 2)}
    ${expandValue(sizes.space * 4)};
  @media (min-width: ${expandValue(breakpoints[3])}) {
    padding: ${expandValue(sizes.space * 6)} ${expandValue(sizes.space * 4)}
      ${expandValue(sizes.space * 4)};
  }

  &::before {
    content: " ";
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    background: linear-gradient(rgba(18, 16, 16, 0) 50%, rgba(0, 0, 0, 0.6) 50%),
      linear-gradient(
        90deg,
        rgba(255, 0, 0, 0.06),
        rgba(0, 255, 0, 0.02),
        rgba(0, 0, 255, 0.06)
      );
    z-index: 2000;
    background-size: 100% 2px, 2px 100%;
    pointer-events: none;
  }
  .crt::after {
    content: " ";
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    background: rgba(18, 16, 16, 0.1);
    opacity: 1;
    z-index: 2001;
    pointer-events: none;
  }
`;
let TerminalContainer = styled("div")`
  ${TerminalContainerStyles}
`;

let GridStyles = props => css`
  height: 100%;
  @media (min-width: ${expandValue(breakpoints[2])}) {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr;
    grid-template-columns: 1lr 3lr;
    grid-column-gap: ${expandValue(sizes.space * 2)};
  }
`;
let Grid = styled("div")`
  ${GridStyles}
`;

let TerminalStyles = props => css`
  height: 100%;
  font-size: ${expandValue(sizes.space * 0.625)};
  line-height: ${expandValue(sizes.space)};
  white-space: pre-wrap;
  font-family: monospace;

  text-shadow: 1.5px 0 1px
      ${tinycolor(props.theme.colors.secondary)
        .setAlpha(0.2)
        .toRgbString()},
    -1.4px 0 1px ${tinycolor(props.theme.colors.secondary)
        .setAlpha(0.2)
        .toRgbString()},
    0 0 2px;

  .Cursor {
    display: inline-block;
    color: ${props.theme.colors.primary};
  }
  .Cursor--blinking {
    animation: blinkingText 0.8s infinite;
  }
  @keyframes blinkingText {
    40% {
      color: ${props.theme.colors.primary};
    }
    79% {
      color: transparent;
    }
    80% {
      color: transparent;
    }
    99% {
      color: transparent;
    }
    100% {
      color: ${props.theme.colors.primary};
    }
  }
  &.hidecursor .Cursor--blinking {
    display: none;
  }
  a {
    color: ${tinycolor(props.theme.colors.primary)
      .lighten(10)
      .brighten(7.5)
      .toString()};
    ${transition("all", sizes.timer + "ms")}
    text-decoration: none;
    &:hover {
      color: ${props.theme.colors.secondary};
    }
    &:focus {
      color: ${props.theme.colors.primary};
      text-decoration: underline;
    }
  }

  .prompt {
    color: ${tinycolor(props.theme.colors.primary)
      .lighten(10)
      .brighten(7.5)
      .toString()};
    margin-right: ${expandValue(sizes.space * 0.5)};
    display: inline-block;
    &.prompt-error {
      color: ${tinycolor(props.theme.colors.secondary).toString()};
    }
  }
  .message {
    &.message-test {
      color: ${tinycolor(props.theme.colors.primary)
        .lighten(20)
        .brighten(15)
        .toString()};
    }
    &.message-error {
      color: ${tinycolor(props.theme.colors.secondary)
        .lighten(15)
        .brighten(9.5)
        .toString()};
    }
    &.message-response {
      color: ${tinycolor(props.theme.colors.primary)
        .lighten(15)
        .brighten(9.5)
        .toString()};
    }
    &.message-chat {
      background: ${tinycolor(props.theme.colors.primary)
        .darken(30)
        .saturate(9.5)
        .toString()};
      color: ${tinycolor
        .mostReadable(
          tinycolor(props.theme.colors.primary)
            .darken(45)
            .saturate(9.5)
            .toString(),
          [props.theme.colors.white, props.theme.colors.black],
          { level: "AAA", size: "large" }
        )
        .toString()};
      padding: ${expandValue(sizes.space * 0.125)} 0;
    }
  }
  .error {
    color: ${tinycolor(props.theme.colors.secondary)
      .lighten(10)
      .brighten(7.5)
      .toString()};
  }
  .success {
    color: ${tinycolor(props.theme.colors.primary)
      .lighten(10)
      .brighten(7.5)
      .toString()};
  }
  .sub-data {
    margin-bottom: ${expandValue(sizes.space * 2)};
    color: ${tinycolor(props.theme.colors.primary)
      .lighten(10)
      .setAlpha(0.5)
      .toString()};
    margin-top: ${expandValue(sizes.space)};
    font-size: ${expandValue(sizes.space * 0.375)};
    line-height: ${expandValue(sizes.space * 0.5)};
  }

  .terminal-input {
    opacity: 0;
    &.active {
      opacity: 1;
    }
    input {
      opacity: 0;
      position: absolute;
      z-index: -10;
    }
    .Cursor {
      opacity: 0;
    }
    &.focused {
      .Cursor {
        opacity: 1;
      }
    }
  }
`;
let TerminalStyled = styled("div")`
  ${TerminalStyles}
`;

let AsciiArtStyles = props => css`
  white-space: pre-wrap;
  font-family: monospace;
  font-size: ${expandValue(sizes.space * 0.875)};
  line-height: ${expandValue(sizes.space)};
  color: ${tinycolor(props.theme.colors.secondary)
    .lighten(10)
    .brighten(7.5)
    .toString()};
  span {
    color: ${tinycolor(props.theme.colors.primary)
      .lighten(10)
      .brighten(7.5)
      .toString()};
  }
  a {
    color: ${tinycolor(props.theme.colors.primary)
      .lighten(10)
      .brighten(7.5)
      .toString()};
    ${transition("all", sizes.timer + "ms")}
    text-decoration: none;
    &:hover {
      color: ${props.theme.colors.secondary};
    }
    &:focus {
      color: ${props.theme.colors.primary};
      text-decoration: underline;
    }
  }
`;
let AsciiArtStyled = styled("div")`
  ${AsciiArtStyles}
`;

export class PageFourZeroFour extends React.Component {
  constructor(props) {
    super();
    this.state = {
      terminalInput: "",
      terminalReturn: [],
      subTerminalReturn: [],
      terminalHistory: [],
      terminalTab: 0,
      redirect: false,
      url: ""
    };

    const responses = [
      "<verb ing> <noun vehicle> <prefix position> <noun container> \u2026 ^^Done",
      "<verb legal ing> <noun animal> <noun surface> \u2026 !!ERR",
      "<verb pose ing> <noun plural> <place> \u2026 !!ERR",
      "<verb walk ing> <prefix> <noun> \u2026 ^^Done"
    ];
    for (var index = 0; index < 3; index++) {
      var response = responses[getRandomInt(0, responses.length - 1)];
      this.state.subTerminalReturn.push({
        type: "test",
        message: rant(response)
      });
    }

    this.state.subTerminalReturn.push(
      {
        type: "break",
        message: ""
      },
      {
        type: "Operating System",
        message: window.navigator.platform
      },
      {
        type: "Client",
        message: window.navigator.appName
      },
      {
        type: "Language",
        message: window.navigator.language
      },
      {
        type: "Browser CodeName",
        message: window.navigator.appCodeName
      },
      {
        type: "Browser Name",
        message: window.navigator.appName
      },
      {
        type: "Browser Version",
        message: window.navigator.appVersion
      }
    );
    this.state.terminalReturn.push(
      {
        type: "error",
        message: "404: OH, snap!"
      },
      {
        type: "error",
        message: "Page requested not found: " + window.location.pathname
      },
      {
        type: "chat",
        message: "Type 'help' for available commands."
      }
    );
  }

  componentDidMount() {
    const options = {
      // duration of the scroll per 1000px, default 500
      speed: 0,
      // minimum duration of the scroll
      minDuration: 0,
      // maximum duration of the scroll
      maxDuration: 0
    };
    animateScrollTo(0, options);

    $("#Top").addClass("Banished");

    this.activateInput();

    /*
    setTimeout(function() {
      this.state.terminalReturn.push(
        {
          type: 'chat',
          message: "How embarrassing. I seemed to have 404'd myself."
        }
      )
      this.forceUpdate()
    }.bind(this), getRandomInt(1000,2500))

    setTimeout(function() {
      this.state.terminalReturn.push(
        {
          type: 'chat',
          message: "Let me see if I can figure out what happened..."
        }
      )
      this.forceUpdate()
    }.bind(this), getRandomInt(4000,5000))

    setTimeout(function() {
      this.state.terminalReturn.push(
        {
          type: 'chat',
          message: "Ah, I see. You were trying to go to "+window.location.pathname+" and that page doesn't exist."
        }
      )
      this.forceUpdate()
    }.bind(this), getRandomInt(8000,9000))


    setTimeout(function() {
      this.state.subTerminalReturn.push(
        {
          type: 'error',
          message: "ERROR! ERROR! ERROR!" 
        }
      )
      this.forceUpdate()
    }.bind(this), getRandomInt(8000,9000))

    setTimeout(function() {
      this.state.terminalReturn.push(
        {
          type: 'chat',
          message: "Sorry."
        }
      )
      this.forceUpdate()
    }.bind(this), getRandomInt(12000,14000))
    */
  }

  activateInput = () => {
    $("#terminalInput").addClass("active");
    $("#terminalInput").addClass("focused");
    this.textInput.focus();
  };
  deactivateInput = () => {
    $("#terminalInput").removeClass("active");
    $("#terminalInput").removeClass("focused");
    this.textInput.blur();
  };

  updateInput = () => {
    this.setState({
      terminalInput: this.textInput.value
    });
  };

  submitInput = e => {
    const options = {
      // duration of the scroll per 1000px, default 500
      speed: 0,
      // minimum duration of the scroll
      minDuration: 0,
      // maximum duration of the scroll
      maxDuration: 0
    };
    if (e.key === "ArrowUp") {
      if (this.state.terminalTab < this.state.terminalHistory.length) {
        this.state.terminalTab = this.state.terminalTab + 1;
        this.state.terminalInput = this.state.terminalHistory[
          this.state.terminalHistory.length - this.state.terminalTab
        ];
        this.forceUpdate();
      }
    }
    if (e.key === "ArrowDown") {
      if (this.state.terminalTab > 0) {
        this.state.terminalTab = this.state.terminalTab - 1;
        this.state.terminalInput = this.state.terminalHistory[
          this.state.terminalHistory.length - this.state.terminalTab
        ];
        this.forceUpdate();
      }
    }
    if (e.key === "Enter") {
      if (this.textInput.value !== "") {
        let textInput = this.textInput.value.split(" ");
        // Disable input
        this.deactivateInput();
        // Add to terminal history
        this.state.terminalHistory.push(this.textInput.value);

        if (
          textInput[0] === "help" ||
          textInput[0] === "-H" ||
          textInput[0] === "Help"
        ) {
          this.state.terminalReturn.push(
            {
              type: "prompt",
              message: this.textInput.value
            },
            {
              type: "response",
              message: "JesseStewart 404 page help text."
            },
            {
              type: "response",
              message: "    clear     -c    Clears terminal window"
            },
            {
              type: "response",
              message: "    help      -H    Display this help text"
            },
            {
              type: "response",
              message: "    history   -h    Show history of commands entered"
            },
            {
              type: "response",
              message: "    ls              List of available programs"
            },
            {
              type: "response",
              message: "    run       -r    Run a program from the list"
            }
          );
        } else if (textInput[0] === "clear" || textInput[0] === "-c") {
          // Clears both the screen and command history
          this.state.terminalReturn = [];
          this.state.terminalHistory = [];
        } else if (textInput[0] === "run" || textInput[0] === "-r") {
          if (!textInput[1].startsWith("/")) {
            textInput[1] = "/" + textInput[1];
          }
          this.textInput.value.split(" ");
          this.setRedirect(textInput[1]);
        } else if (textInput[0] === "ls") {
          this.state.terminalReturn.push(
            {
              type: "prompt",
              message: this.textInput.value
            },
            {
              type: "response",
              message: "    /          Home page"
            },
            {
              type: "response",
              message: "    work      List of selected portfolio pieces"
            },
            {
              type: "response",
              message: "     \u21B3 work/fracturedveil"
            },
            {
              type: "response",
              message: "     \u21B3 work/kuaa"
            },
            {
              type: "response",
              message: "     \u21B3 work/klymit"
            },
            {
              type: "response",
              message: "     \u21B3 work/mcgee"
            },
            {
              type: "response",
              message: "     \u21B3 work/ogg"
            },
            {
              type: "response",
              message: "     \u21B3 work/blackdiamond"
            },
            {
              type: "response",
              message: "     \u21B3 work/blackdiamonds"
            },
            {
              type: "response",
              message: "     \u21B3 work/chapul"
            },
            {
              type: "response",
              message: "     \u21B3 work/leaf"
            },
            {
              type: "response",
              message: "     \u21B3 work/manimal"
            },
            {
              type: "response",
              message: "     \u21B3 work/mbj"
            },
            {
              type: "response",
              message: "     \u21B3 work/olympic"
            },
            {
              type: "response",
              message: "     \u21B3 work/sharpko"
            },
            {
              type: "response",
              message: "     \u21B3 work/tucker"
            },
            {
              type: "response",
              message: "     \u21B3 work/pcg"
            },
            {
              type: "response",
              message: "    resume    Resume of Jesse Stewart"
            },
            {
              type: "response",
              message: "    contact   Contact information for Jesse Stewart"
            },
            {
              type: "response",
              message: "    todo      List of pending items and website credits"
            }
          );
        } else if (textInput[0] === "history" || textInput[0] === "-h") {
          this.state.terminalReturn.push({
            type: "prompt",
            message: this.textInput.value
          });
          for (
            var index = 0;
            index < this.state.terminalHistory.length;
            index++
          ) {
            this.state.terminalReturn.push({
              type: "response",
              message: this.state.terminalHistory[index]
            });
          }
        } else {
          this.state.terminalReturn.push(
            {
              type: "prompt",
              message: this.textInput.value
            },
            {
              type: "error",
              message: "Command not found"
            }
          );
          this.state.subTerminalReturn.push({
            type: "error",
            message: "Command not found"
          });
        }
        this.setState({
          terminalInput: "",
          terminalTab: 0
        });
        this.activateInput();

        $("html, body").animate({ scrollTop: $(document).height() }, "slow");
      }
    }
  };

  colorize = s => {
    let colorized = s;
    if (colorized.indexOf("!!") !== -1) {
      colorized = colorized.split("!!");
      return colorized;
    } else if (colorized.indexOf("^^") !== -1) {
      colorized = colorized.split("^^");
      return colorized;
    } else {
      return colorized;
    }
  };

  setRedirect = u => {
    this.setState({
      redirect: true,
      url: u
    });
  };
  renderRedirect = () => {
    if (this.state.redirect) {
      return <Redirect to={this.state.url} />;
    }
  };

  render() {
    return (
      <TerminalContainer>
        <Helmet>
          <title>ERROR 404 - Jesse Stewart</title>
        </Helmet>
        {this.renderRedirect()}
        <Global
          styles={css`
            .header,
            .copyright,
            .nav-link {
              font-family: monospace !important;
            }
          `}
        />

        <Grid>
          <div style={{ gridColumn: "1/3", gridRow: "1/3" }}>
            <AsciiArtStyled>
              ░░░░░░░░░░░░░░░░░░
              <br />
              ░░░░<span>█</span>░░░<span>██</span>░░░░░<span>█</span>░░
              <br />
              ░░░<span>██</span>░░<span>█</span>░░<span>█</span>░░░
              <span>██</span>░░
              <br />
              ░░<span>█</span>░<span>█</span>░░<span>█</span>░░<span>█</span>░░
              <span>█</span>░<span>█</span>░░
              <br />░<span>█████</span>░<span>█</span>░░<span>█</span>░
              <span>█████</span>░<br />
              ░░░░<span>█</span>░░░<span>██</span>░░░░░<span>█</span>░░
              <br />
              ░░░░░░░░░░░░░░░░░░
              <br />
              <br />
            </AsciiArtStyled>
            {/*
            <TerminalStyled id="terminal-typist">
              <div className="sub-data">
                {this.state.subTerminalReturn.map((subTerminalLine, index) =>
                  <React.Fragment>
                    {subTerminalLine.type === 'test' ?
                      <span key={"term_"+index}>
                        <Typist 
                          avgTypingDelay={15} 
                          stdTypingDelay={15} 
                          onTypingDone={this.activateInput}
                          cursor={{ show:false}}
                          >
                            &#187;&nbsp;
                            {this.colorize(subTerminalLine.message)[0]}
                            <Typist.Delay ms={getRandomInt(1000,1500)} />
                            {this.colorize(subTerminalLine.message)[1] === 'ERR' ?
                              <span className="error">&#33;{this.colorize(subTerminalLine.message)[1]}</span>
                            : this.colorize(subTerminalLine.message)[1] === 'Done' &&
                              <span className="success">{this.colorize(subTerminalLine.message)[1]}</span>
                            }
                          <br/>
                        </Typist>
                      </span>
                    : subTerminalLine.type === 'break' ?
                      <br/>
                    :
                      <Typist 
                        avgTypingDelay={1} 
                        stdTypingDelay={1} 
                        cursor={{
                          show: true,
                          blink: true,
                          element: '▁',
                          hideWhenDone: true,
                          hideWhenDoneDelay: 0}}
                      >
                        <span key={"subterm_"+index}>
                          &#187;&nbsp;<strong>{subTerminalLine.type}:</strong> <Typist.Delay ms={getRandomInt(0,500)} />{subTerminalLine.message}<br/>
                        </span>
                      </Typist>
                    }
                  </React.Fragment>
                )}
              </div>
            </TerminalStyled>
            */}
          </div>
          <div style={{ gridColumn: "3/7", gridRow: "1/3", height: "100%" }}>
            <TerminalStyled id="terminal-typist" onClick={this.activateInput}>
              {/*
              <Typist 
                avgTypingDelay={15} 
                stdTypingDelay={15} 
                onTypingDone={this.activateInput}
                cursor={{
                  show: true,
                  blink: true,
                  element: '▁',
                  hideWhenDone: true,
                  hideWhenDoneDelay: 0}}
                
              >
                <strong><span className="error">That page doesn't exist!</span></strong><br/><br/>
                <strong>Requested URL:</strong> <a>{window.location.href}</a><br/><br/>
                <span className="prompt">JS&nbsp;&#x291A;</span><Typist.Delay ms={1200} />I promise I will do something clever here&mdash;I'm just out of sorts right now. <Typist.Delay ms={700} />Send hate mail to: <a href="mailto:hate@jessestewart.com" target="_blank" rel="noopener noreferrer">hate@jessestewart.com</a><br/><br/>
              </Typist>
              */}

              {this.state.terminalReturn.map((terminalLine, index) => (
                <span key={"term_" + index}>
                  {terminalLine.type === "test" ? (
                    <Typist
                      avgTypingDelay={15}
                      stdTypingDelay={15}
                      onTypingDone={this.activateInput}
                      cursor={{ show: false }}
                    >
                      <span className="prompt prompt-test">
                        &nbsp;&nbsp;&nbsp;&#187;
                      </span>
                      <span className="message message-test">
                        {this.colorize(terminalLine.message)[0]}
                        <span className="message">
                          <Typist.Delay ms={getRandomInt(1000, 1500)} />
                          {this.colorize(terminalLine.message)[1] === "ERR" ? (
                            <span className="error">
                              &#33;{this.colorize(terminalLine.message)[1]}
                            </span>
                          ) : (
                            this.colorize(terminalLine.message)[1] ===
                              "Done" && (
                              <span className="success">
                                {this.colorize(terminalLine.message)[1]}
                              </span>
                            )
                          )}
                        </span>
                      </span>
                      <br />
                    </Typist>
                  ) : terminalLine.type === "prompt" ? (
                    <span>
                      <span className="prompt">JS&nbsp;&#8250;</span>
                      {terminalLine.message}
                      <br />
                    </span>
                  ) : terminalLine.type === "error" ? (
                    <Typist
                      avgTypingDelay={15}
                      stdTypingDelay={15}
                      onTypingDone={this.activateInput}
                      cursor={{
                        show: true,
                        blink: true,
                        element: "▁",
                        hideWhenDone: true,
                        hideWhenDoneDelay: 0
                      }}
                    >
                      <span className="prompt prompt-error">ERR&#33;</span>
                      <span className="message message-error">
                        {terminalLine.message}
                      </span>
                      <br />
                    </Typist>
                  ) : terminalLine.type === "response" ? (
                    <Typist
                      avgTypingDelay={1}
                      stdTypingDelay={1}
                      onTypingDone={this.activateInput}
                      cursor={{
                        show: true,
                        blink: true,
                        element: "▁",
                        hideWhenDone: true,
                        hideWhenDoneDelay: 0
                      }}
                    >
                      <span className="prompt prompt-response">
                        &nbsp;&nbsp;&nbsp;&nbsp;
                      </span>
                      <span className="message message-response">
                        {terminalLine.message}
                      </span>
                      <br />
                    </Typist>
                  ) : terminalLine.type === "chat" ? (
                    <Typist
                      avgTypingDelay={25}
                      stdTypingDelay={25}
                      onTypingDone={this.activateInput}
                      cursor={{ show: false }}
                    >
                      <span className="prompt prompt-chat">
                        &nbsp;&nbsp;&nbsp;
                      </span>
                      <span className="message message-chat">
                        &nbsp;{terminalLine.message}&nbsp;
                      </span>
                      <br />
                    </Typist>
                  ) : (
                    <Typist
                      avgTypingDelay={15}
                      stdTypingDelay={15}
                      onTypingDone={this.activateInput}
                      cursor={{
                        show: true,
                        blink: true,
                        element: "▁",
                        hideWhenDone: true,
                        hideWhenDoneDelay: 0
                      }}
                    >
                      <span className="prompt">&nbsp;&nbsp;&nbsp;&nbsp;</span>
                      <span className="message">{terminalLine.message}</span>
                      <br />
                    </Typist>
                  )}
                </span>
              ))}

              <div className="terminal-input" id="terminalInput">
                <span className="prompt">JS&nbsp;&#8250;</span>
                {this.state.terminalInput}
                <span className="Cursor Cursor--blinking">▁</span>
                <input
                  type="text"
                  value={this.state.terminalInput || ""}
                  ref={input => (this.textInput = input)}
                  onChange={this.updateInput}
                  onFocus={this.inputFocus}
                  onBlur={this.inputBlur}
                  onKeyDown={this.submitInput}
                />
              </div>
            </TerminalStyled>
          </div>
        </Grid>
      </TerminalContainer>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/Pages/FourZeroFour.js